import { EGender } from 'affiny-core/modules/account/types';
import React, { lazy, memo, Suspense } from 'react';
import styled from 'styled-components';
import { ColorProps, SpaceProps } from 'styled-system';
import rem from '../../utils/rem';
import { Fallback } from '../fallback';
import { Box, BoxProps } from '../primitives';

const UserPicture = lazy(() => import('./components/user-picture'));
const Placeholder = lazy(() => import('./components/placeholder'));
const PrivatePic = lazy(() => import('./components/private-pic'));
const Overlay = lazy(() => import('./components/overlay'));
const Pin = lazy(() => import('../pin/pin'));
const ModerationOverlay = lazy(
  () => import('../moderation-overlay/moderation-overlay')
);

export type AvatarProps = SpaceProps &
  Pick<ColorProps, 'opacity'> & {
    size: number;
    gender?: EGender;
    isCircle?: boolean;
    hasPin?: boolean;
    avatarStyle?: Omit<BoxProps, 'color'>;
    hasPrivatePicture?: boolean;
    pictureId?: string;
    pictureUri?: string;
    nbHiddenPic?: number;
    onClick?: () => void;
    isBlur?: boolean;
    onModeration?: boolean;
    children?: React.ReactNode;
  };

const Avatar = ({
  pictureUri,
  hasPrivatePicture,
  size,
  isCircle = true,
  hasPin = false,
  gender,
  nbHiddenPic,
  avatarStyle,
  onClick,
  isBlur = false,
  onModeration,
  children,
  ...rest
}: AvatarProps) => (
  <Box
    size={rem(size)}
    onClick={onClick}
    cursor={onClick ? 'pointer' : 'inherit'}
    {...rest}
  >
    <Box
      size={rem(size)}
      borderRadius={isCircle ? '50%' : 'small'}
      overflow="hidden"
      {...avatarStyle}
    >
      <SBlur isBlur={isBlur}>
        <Suspense fallback={<Fallback />}>
          {pictureUri ? (
            <>
              <UserPicture uri={pictureUri} isCircle={isCircle} />
              {onModeration && <ModerationOverlay size={size} />}
            </>
          ) : (
            <Placeholder
              isCircle={isCircle}
              nbHiddenPic={nbHiddenPic}
              isUnisex={!gender}
              {...(gender && { gender })}
            />
          )}
        </Suspense>

        {hasPrivatePicture && !children && (
          <Suspense fallback={<Fallback />}>
            <PrivatePic size={size} />
          </Suspense>
        )}

        {!!children &&
          typeof children === 'function' &&
          children({ hasPrivatePicture })}
        {!!children && typeof children !== 'function' && (
          <Suspense fallback={<Fallback />}>
            <Overlay>{children}</Overlay>
          </Suspense>
        )}
      </SBlur>
    </Box>

    {hasPin && (
      <Suspense fallback={<Fallback />}>
        <Pin top={rem(2)} right={rem(2)} />
      </Suspense>
    )}
  </Box>
);

const SBlur = styled(Box)<{ isBlur: boolean }>`
  ${({ isBlur }) =>
    isBlur &&
    `filter: blur(10px); 
    opacity: .6`};
  width: 100%;
  height: 100%;
`;

export default memo(Avatar);
